/**
 * Dropdown Directive patch to handle popover position
 */
import {
  AfterContentInit,
  ContentChild,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';

import { Observable, of as observableOf, Subscription } from 'rxjs';
import { delay, first, map, tap } from 'rxjs/operators';

const className = 'dropdown-full-show';

@Directive({
  // eslint-disable-next-line
  selector: '[dropdownMenu]',
})
export class XVIDropdownMenuDirective {
  @Input() public set dropdownMenu(relativeParentSelector) {
    this.relativeParentSelector = relativeParentSelector;
  }
  @Input() public set dropdownMenuParentGap(parentGap) {
    this.parentGap = parentGap;
  }
  @Input() public set dropdownMenuScrollGap(scrollGap) {
    this.scrollGap = scrollGap;
  }
  @Input() public set dropdownMenuFixedTop(fixedTop) {
    this.fixedTop = fixedTop;
  }
  public container: string;
  private relativeParentSelector: string = '';
  private scrollGap: number = 70;
  private parentGap: number = 20;
  // This is the sum of our header and breadcrumb heights
  private fixedTop: number = 99;

  public constructor(private element: ElementRef) {}

  /**
   * Update Popover element position and return available scrollY range
   *
   * @return Available scrollY range
   */
  public updatePosition(): Observable<{ min: number; max: number }> {
    // Note: I don't want to get overflow parent element and popover and calculate
    // available scrollY range on each scroll event

    // Use Observable here to remove setTimeout()
    let overflowParent;
    let originOverflowY;

    return observableOf({}).pipe(
      tap(() => {
        overflowParent = this.getOverflowParent();
        // Sometimes scroll blinking appears
        if (overflowParent) {
          originOverflowY = overflowParent.style.overflowY;
          overflowParent.style.overflowY = 'hidden';
        }
      }),
      delay(0),
      map(() => {
        const menuElement = this.getMenuElement();
        const originDisplay = menuElement.style.display;
        menuElement.style.display = 'none';

        const bounces = this.getOverflowParentBounces(overflowParent);

        // If we start use this property in some case for the popover
        // we should rethink this approach with saving origin transform value
        // I don't want to use marginTop for this purposes because it could be in percents
        menuElement.style.transform = '';
        menuElement.style.display = originDisplay;
        const rect = menuElement.getBoundingClientRect();

        const offset = this.getOffset(rect, bounces);

        menuElement.style.transform = `translate(0, -${offset}px)`;

        return this.getScrollRange();
      }),
      tap(() => {
        this.getMenuElement().style.opacity = '1';
        if (overflowParent) {
          overflowParent.style.overflowY = originOverflowY;
        }
      }),
      tap(() => {
        // If there is no action item, the action panel will be hidden.
        const menuElement = this.getMenuElement();
        const dropdownContainerEl: HTMLElement = document.querySelector(
          'bs-dropdown-container',
        );
        const isNoOperationItems = menuElement.childElementCount === 0;

        if (isNoOperationItems) {
          dropdownContainerEl.style.display = 'none';
        }
      }),
    );
  }

  /**
   * Hide popover for the next openings
   */
  public close(): void {
    const element = this.getMenuElement();
    if (element) {
      element.style.opacity = '0';
    }
  }

  /**
   * Return dropdown popover HTML element
   *
   * @returns
   */
  private getMenuElement(): HTMLElement {
    return (
      this.element.nativeElement.nextElementSibling ||
      document.querySelector('bs-dropdown-container .dropdown-menu')
    );
  }

  /**
   * Find parent element with overflow style
   *
   * @returns
   */
  private getOverflowParent(): HTMLElement | null {
    if (this.container) {
      const element = this.getMenuElement();

      return element.closest(this.container);
    }

    let overflowParent = this.element.nativeElement.parentElement;

    while (
      overflowParent !== null &&
      overflowParent.style &&
      overflowParent.style.overflowY !== 'hidden' &&
      // Sometimes elements with overflow 'hidden auto' have effect of cutting
      // popover. We don't want to change styles of another components
      overflowParent.style.overflowX !== 'hidden' &&
      (!this.relativeParentSelector ||
        !overflowParent.matches(this.relativeParentSelector))
    ) {
      overflowParent = overflowParent.parentElement;
    }

    return overflowParent;
  }

  /**
   * Find bounces of show
   *
   * @param overflowParent {HTMLElement | null} Parent element with overflow styles
   *
   * @returns
   */
  private getOverflowParentBounces(overflowParent: HTMLElement | null): any {
    let bounces = overflowParent
      ? overflowParent.getBoundingClientRect()
      : {
          height: window.innerHeight,
          top: 0,
          bottom: window.innerHeight,
          visibleHeight: 0,
        };

    // getBoundingClientRect returns object with readonly properties
    // Object.assign doesn't help
    bounces = {
      height: bounces.height,
      top: bounces.top,
      bottom: bounces.bottom,
      visibleHeight: 0,
    };

    // If overflow parent is out of screen we should check by screen bounce
    if (bounces.bottom > window.innerHeight) {
      bounces.bottom = window.innerHeight;
    }

    // Find available height to show
    bounces.visibleHeight = bounces.bottom - bounces.top;

    return bounces;
  }

  /**
   * Find offset of popover element
   *
   * @param rect Popover element bounces
   * @param bounces Available bounces
   * @returns
   */
  private getOffset(rect, bounces): number {
    // If popover on the screen
    if (rect.bottom + this.parentGap < bounces.bottom) {
      return 0;
    }

    // Check that popover has enough height to be shown
    let offset =
      bounces.visibleHeight < rect.height + 2 * this.parentGap
        ? // show in the top of bounces
          rect.top - bounces.top - this.parentGap
        : // find differences of bounce bottom and popover bottom
          rect.bottom - bounces.bottom + this.parentGap;

    const resultTop = rect.top - offset;

    // We don't want that popover is under breadcrumbs that have fixed position
    if (resultTop < this.fixedTop + this.parentGap) {
      offset -= this.fixedTop + this.parentGap - resultTop;
    }

    return offset;
  }

  /**
   * Return available scroll range to show popover
   *
   * @returns
   */
  private getScrollRange(): { min: number; max: number } {
    // Find scroll value
    const rect = this.getMenuElement().getBoundingClientRect();

    const maxScroll =
      rect.bottom < window.innerHeight
        ? window.scrollY + this.scrollGap
        : window.scrollY + rect.bottom - window.innerHeight + this.scrollGap;

    return {
      min: window.scrollY - this.scrollGap,
      max: maxScroll,
    };
  }
}

@Directive({
  // eslint-disable-next-line
  selector: '[dropdown]',
})
export class XviDropdownDirective
  implements OnInit, AfterContentInit, OnDestroy
{
  @Input() public hideOnScroll: boolean = true;
  @Input() public container: string;

  @ContentChild(BsDropdownDirective, { static: false })
  private dropdownDirective: BsDropdownDirective;
  @ContentChild(XVIDropdownMenuDirective, { static: false })
  private menuDirective: XVIDropdownMenuDirective;

  private onShownSubscription: Subscription;
  private onHiddenSubscription: Subscription;
  private allowedScrollPosition: any;

  public constructor(private element: ElementRef) {}

  public ngOnInit(): void {
    this.element.nativeElement.classList.add(className);
  }

  @HostListener('window:scroll', [])
  public onWindowScroll(): void {
    if (!this.dropdownDirective?.isOpen || !this.hideOnScroll) {
      return;
    }

    if (
      window.scrollY < this.allowedScrollPosition.min ||
      window.scrollY > this.allowedScrollPosition.max
    ) {
      this.dropdownDirective.hide();
    }
  }

  public ngAfterContentInit(): void {
    if (!this.dropdownDirective) {
      return null;
    }
    this.onShownSubscription = this.dropdownDirective.onShown.subscribe(() => {
      this.menuDirective
        .updatePosition()
        .pipe(first())
        .subscribe((allowedScrollPosition) => {
          this.allowedScrollPosition = allowedScrollPosition;
        });
    });
    this.onHiddenSubscription = this.dropdownDirective.onHidden.subscribe(
      () => {
        this.menuDirective.close();
      },
    );
    if (!!this.menuDirective) {
      this.menuDirective.container = this.container;
    }
  }

  public ngOnDestroy(): void {
    if (!this.onShownSubscription) {
      return null;
    }
    this.onShownSubscription.unsubscribe();
    this.onHiddenSubscription.unsubscribe();
  }
}
